import { useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import BrandLogo from "@/components/BrandLogo";
import { goBackOr } from "@/lib/goBackOr";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submitLock = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Connexion réussie");
      const ret = location.state?.from;
      if (ret?.pathname) {
        navigate(`${ret.pathname}${ret.search || ""}`, { replace: true });
      } else {
        navigate(u?.role === "admin" ? "/admin" : "/marketplace", { replace: true });
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : err?.message;
      toast.error(msg || "Échec de connexion");
    } finally {
      setLoading(false);
      submitLock.current = false;
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo
        title="Connexion"
        description="Connexion sécurisée à votre espace RdvMarket."
        canonicalPath="/login"
        robots="noindex, nofollow"
      />
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-[#2D7FF9]/70 mix-blend-multiply" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <BrandLogo
            to="/"
            size="md"
            showWordmark
            wordmarkClassName="font-black text-2xl tracking-[-0.03em] text-white"
            data-testid="auth-logo"
          />
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-widest opacity-80 mb-4">Marketplace vérifiée</div>
            <h2 className="font-display font-black text-4xl leading-tight mb-4">Rendez-vous qualifiés. Zéro fraude. Paiement sécurisé.</h2>
            <p className="text-white/80">
              Encaissement vendeur via Stripe Connect · Anonymisation totale · Paiement en séquestre · Arbitrage humain en 72h.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-8 sm:p-8">
        <div className="w-full min-w-0 max-w-sm">
          <button
            type="button"
            className="mb-8 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#2D7FF9]"
            data-testid="back-home"
            onClick={() => goBackOr(navigate)}
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-8">Bon retour parmi nous.</h1>

          <form onSubmit={submit} className="space-y-4 mb-6">
            <div>
              <Label htmlFor="email" required>
                E-mail
              </Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" required>
                Mot de passe
              </Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="btn-primary w-full h-11 rounded-md" disabled={loading} data-testid="login-submit">
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-8">
            Pas encore de compte ?{" "}
            <Link to="/register" state={location.state} className="text-[#2D7FF9] font-semibold hover:underline" data-testid="goto-register">Créer un compte</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
